/**
 * React hook for Session Level and Options Level Indicators
 *
 * Fetches and manages:
 * - Previous Day Levels (PDH, PDL, PDC)
 * - Asia/London Session Levels (ASH/ASL, LDH/LDL)
 * - Opening Range (OR High, Low, Mid)
 * - Initial Balance (IB High, Low, Mid)
 * - Zero Levels (GEX/DEX/VEX/CEX zero crossings)
 * - Expected Move (IV-based bands; own endpoint, expiry-mode independent)
 * - Gamma Levels (GVWAP, band, walls)
 *
 * Each family is fetched from its own endpoint in parallel and the per-day
 * values spread-merge by their root keys.
 */

import { useState, useEffect, useCallback } from 'react';
import CONFIG from '../config';
import { authFetch } from '../auth/authFetch';
import { getLatestSessionDate, getOpenSessionDate } from '../utils/market';

/**
 * Hook for fetching and managing session level indicators
 *
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {Object} options - Configuration options
 * @param {string} options.endDate - End date in YYYY-MM-DD format (defaults to startDate)
 * @param {string} options.apiUrl - API base URL
 * @returns {Object} - Level data and state
 */
export function useLevelIndicators(startDate, options = {}) {
  // Multi-day state (keyed by session date, each day carries time boundaries)
  const [levelsByDay, setLevelsByDay] = useState({});

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const apiUrl = options.apiUrl || CONFIG.apiUrl;
  const endDate = options.endDate || startDate;
  const expiryMode = options.expiryMode || '0dte';
  const underlying = options.underlying || 'spx';
  const enabled = options.enabled !== undefined ? options.enabled : true;
  const fetchSession = options.fetchSession !== undefined ? options.fetchSession : true;
  const fetchZero = options.fetchZero !== undefined ? options.fetchZero : true;
  // Expected Move and Gamma Levels default OFF: callers opt in explicitly.
  const fetchEm = options.fetchEm !== undefined ? options.fetchEm : false;
  const fetchGamma = options.fetchGamma !== undefined ? options.fetchGamma : false;

  // Fetch levels from enabled endpoints in parallel
  const fetchLevels = useCallback(async (start, end, wantSession, wantZero, wantEm, wantGamma) => {
    if (!start) return;

    setLoading(true);
    setError(null);

    try {
      const fetches = [];

      if (wantSession) {
        const sessionUrl = `${apiUrl}/api/indicators/session-levels?start_date=${start}&end_date=${end || start}`;
        console.log('[useLevelIndicators] Fetching session levels:', sessionUrl);
        fetches.push(authFetch(sessionUrl));
      } else {
        fetches.push(Promise.resolve(null));
      }

      if (wantZero) {
        const zeroUrl = `${apiUrl}/api/indicators/zero-levels?start_date=${start}&end_date=${end || start}&expiry_mode=${expiryMode}&underlying=${underlying}`;
        console.log('[useLevelIndicators] Fetching zero levels:', zeroUrl);
        fetches.push(
          authFetch(zeroUrl).catch(err => {
            console.warn('[useLevelIndicators] Zero levels fetch failed:', err);
            return null;
          })
        );
      } else {
        fetches.push(Promise.resolve(null));
      }

      if (wantEm) {
        // Expected Move is expiry-mode independent: no expiry_mode param.
        const emUrl = `${apiUrl}/api/indicators/expected-move?start_date=${start}&end_date=${end || start}&underlying=${underlying}`;
        console.log('[useLevelIndicators] Fetching expected move:', emUrl);
        fetches.push(
          authFetch(emUrl).catch(err => {
            console.warn('[useLevelIndicators] Expected move fetch failed:', err);
            return null;
          })
        );
      } else {
        fetches.push(Promise.resolve(null));
      }

      if (wantGamma) {
        const gammaUrl = `${apiUrl}/api/indicators/gamma-levels?start_date=${start}&end_date=${end || start}&expiry_mode=${expiryMode}&underlying=${underlying}`;
        console.log('[useLevelIndicators] Fetching gamma levels:', gammaUrl);
        fetches.push(
          authFetch(gammaUrl).catch(err => {
            console.warn('[useLevelIndicators] Gamma levels fetch failed:', err);
            return null;
          })
        );
      } else {
        fetches.push(Promise.resolve(null));
      }

      const [sessionRes, zeroRes, emRes, gammaRes] = await Promise.all(fetches);

      if (sessionRes && !sessionRes.ok) {
        throw new Error(`Failed to fetch session levels: ${sessionRes.statusText}`);
      }

      const sessionData = sessionRes ? await sessionRes.json() : null;
      const zeroData = zeroRes && zeroRes.ok ? await zeroRes.json() : null;
      const emData = emRes && emRes.ok ? await emRes.json() : null;
      const gammaData = gammaRes && gammaRes.ok ? await gammaRes.json() : null;

      // Merge levels_by_day from all responses (per-day values are pure
      // root-key wrappers, so the spreads namespace by endpoint)
      const mergedLevelsByDay = {};
      const sessionLevels = sessionData?.levels_by_day || {};
      const zeroLevels = zeroData?.levels_by_day || {};
      const emLevels = emData?.levels_by_day || {};
      const gammaLevels = gammaData?.levels_by_day || {};
      const allDates = new Set([
        ...Object.keys(sessionLevels),
        ...Object.keys(zeroLevels),
        ...Object.keys(emLevels),
        ...Object.keys(gammaLevels),
      ]);

      for (const dateKey of allDates) {
        mergedLevelsByDay[dateKey] = {
          ...(sessionLevels[dateKey] || {}),
          ...(zeroLevels[dateKey] || {}),
          ...(emLevels[dateKey] || {}),
          ...(gammaLevels[dateKey] || {}),
        };
      }

      setLevelsByDay(mergedLevelsByDay);

      const dayCount = Object.keys(mergedLevelsByDay).length;
      console.log(`[useLevelIndicators] Fetched levels for ${dayCount} day(s)`, {
        startDate: start,
        endDate: end,
        days: Object.keys(mergedLevelsByDay),
        fetchedSession: wantSession,
        fetchedZero: wantZero,
        fetchedEm: wantEm,
        fetchedGamma: wantGamma,
      });

    } catch (err) {
      console.error('[useLevelIndicators] Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, expiryMode, underlying]);

  // Refetch when dates or toggles change (only if enabled)
  useEffect(() => {
    if (startDate && enabled) {
      fetchLevels(startDate, endDate, fetchSession, fetchZero, fetchEm, fetchGamma);
    } else if (!enabled) {
      // Clear state when disabled to avoid stale data
      setLevelsByDay({});
    }
  }, [startDate, endDate, fetchLevels, enabled, fetchSession, fetchZero, fetchEm, fetchGamma]);

  // Poll while a live session is open and the visible range includes today,
  // so forming windows (overnight, OR, IB) keep updating until they end.
  // The endpoint recomputes the current day fresh on every call, so each
  // poll returns the latest forming extremes. No immediate tick: the effect
  // above already fetches on enable and on date changes.
  useEffect(() => {
    if (!enabled || !fetchSession || !startDate) return;
    const tick = () => {
      const today = getLatestSessionDate();
      const end = endDate || startDate;
      if (!(startDate <= today && today <= end)) return; // viewing history only
      if (getOpenSessionDate() === null) return;         // market closed / break
      fetchLevels(startDate, endDate, fetchSession, fetchZero, fetchEm, fetchGamma);
    };
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [startDate, endDate, enabled, fetchSession, fetchZero, fetchEm, fetchGamma, fetchLevels]);

  // Manual refresh
  const refresh = useCallback(() => {
    if (startDate) {
      fetchLevels(startDate, endDate, fetchSession, fetchZero, fetchEm, fetchGamma);
    }
  }, [startDate, endDate, fetchLevels, fetchSession, fetchZero, fetchEm, fetchGamma]);

  return {
    // Multi-day data with time boundaries
    levelsByDay,
    loading,
    error,
    refresh,
  };
}

export default useLevelIndicators;
